import * as plugins from './webstore.plugins.js';

export interface IWebStoreOptions {
  dbName: string;
  storeName: string;
}

export interface IWebStoreOperationOptions {
  signal?: AbortSignal;
}

export interface IWebStoreDb {
  nativeDb: IDBDatabase;
  get<TValue>(
    storeName: string,
    key: IDBValidKey | IDBKeyRange,
    options?: IWebStoreOperationOptions
  ): Promise<TValue>;
  getKey(
    storeName: string,
    key: IDBValidKey | IDBKeyRange,
    options?: IWebStoreOperationOptions
  ): Promise<IDBValidKey | undefined>;
  put<TValue>(
    storeName: string,
    val: TValue,
    key?: IDBValidKey,
    options?: IWebStoreOperationOptions
  ): Promise<IDBValidKey>;
  delete(
    storeName: string,
    key: IDBValidKey | IDBKeyRange,
    options?: IWebStoreOperationOptions
  ): Promise<void>;
  clear(storeName: string, options?: IWebStoreOperationOptions): Promise<void>;
  getAllKeys(storeName: string, options?: IWebStoreOperationOptions): Promise<IDBValidKey[]>;
  close(): void;
}

class WebStoreDb implements IWebStoreDb {
  constructor(public nativeDb: IDBDatabase) {}

  public async get<TValue>(
    storeName: string,
    key: IDBValidKey | IDBKeyRange,
    options: IWebStoreOperationOptions = {}
  ): Promise<TValue> {
    return this.runObjectStoreRequest(
      storeName,
      'readonly',
      (objectStore) => objectStore.get(key),
      options
    );
  }

  public async getKey(
    storeName: string,
    key: IDBValidKey | IDBKeyRange,
    options: IWebStoreOperationOptions = {}
  ): Promise<IDBValidKey | undefined> {
    return this.runObjectStoreRequest(
      storeName,
      'readonly',
      (objectStore) => objectStore.getKey(key),
      options
    );
  }

  public async put<TValue>(
    storeName: string,
    val: TValue,
    key?: IDBValidKey,
    options: IWebStoreOperationOptions = {}
  ): Promise<IDBValidKey> {
    return this.runObjectStoreRequest(
      storeName,
      'readwrite',
      (objectStore) =>
        key === undefined ? objectStore.put(val) : objectStore.put(val, key),
      options
    );
  }

  public async delete(
    storeName: string,
    key: IDBValidKey | IDBKeyRange,
    options: IWebStoreOperationOptions = {}
  ): Promise<void> {
    await this.runObjectStoreRequest(
      storeName,
      'readwrite',
      (objectStore) => objectStore.delete(key),
      options
    );
  }

  public async clear(
    storeName: string,
    options: IWebStoreOperationOptions = {}
  ): Promise<void> {
    await this.runObjectStoreRequest(
      storeName,
      'readwrite',
      (objectStore) => objectStore.clear(),
      options
    );
  }

  public async getAllKeys(
    storeName: string,
    options: IWebStoreOperationOptions = {}
  ): Promise<IDBValidKey[]> {
    return this.runObjectStoreRequest(
      storeName,
      'readonly',
      (objectStore) => objectStore.getAllKeys(),
      options
    );
  }

  public close(): void {
    this.nativeDb.close();
  }

  private async runObjectStoreRequest<TResult>(
    storeName: string,
    mode: IDBTransactionMode,
    createRequest: (objectStore: IDBObjectStore) => IDBRequest<TResult>,
    options: IWebStoreOperationOptions
  ): Promise<TResult> {
    options.signal?.throwIfAborted();
    const transaction = this.nativeDb.transaction(storeName, mode);
    const objectStore = transaction.objectStore(storeName);
    const request = createRequest(objectStore);
    let rejectForAbort!: (reason?: unknown) => void;
    const abortPromise = new Promise<never>((_resolve, reject) => {
      rejectForAbort = reject;
    });
    const abortTransaction = () => {
      try {
        transaction.abort();
      } catch (error) {
        if (!(error instanceof DOMException) || error.name !== 'InvalidStateError') {
          rejectForAbort(error);
          return;
        }
      }
      if (options.signal) {
        rejectForAbort(options.signal.reason);
      }
    };
    options.signal?.addEventListener('abort', abortTransaction, { once: true });

    try {
      const [result] = await Promise.race([
        Promise.all([
          indexedDbRequestToPromise(request),
          indexedDbTransactionToPromise(transaction),
        ]),
        abortPromise,
      ]);
      return result;
    } catch (error) {
      if (options.signal?.aborted) {
        throw options.signal.reason;
      }
      throw error;
    } finally {
      options.signal?.removeEventListener('abort', abortTransaction);
    }
  }
}

const awaitWithSignal = async <TResult>(
  promiseArg: Promise<TResult>,
  signalArg?: AbortSignal
): Promise<TResult> => {
  signalArg?.throwIfAborted();
  if (!signalArg) {
    return promiseArg;
  }

  let rejectForAbort!: (reason?: unknown) => void;
  const abortPromise = new Promise<never>((_resolve, reject) => {
    rejectForAbort = reject;
  });
  const abortListener = () => {
    rejectForAbort(signalArg.reason);
  };
  signalArg.addEventListener('abort', abortListener, { once: true });
  try {
    return await Promise.race([promiseArg, abortPromise]);
  } finally {
    signalArg.removeEventListener('abort', abortListener);
  }
};

const indexedDbRequestToPromise = <TResult>(request: IDBRequest<TResult>): Promise<TResult> => {
  return new Promise<TResult>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(createIndexedDbError('IndexedDB request failed', request.error));
  });
};

const indexedDbTransactionToPromise = (transaction: IDBTransaction): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(createIndexedDbError('IndexedDB transaction aborted', transaction.error));
    transaction.onerror = () =>
      reject(createIndexedDbError('IndexedDB transaction failed', transaction.error));
  });
};

const createIndexedDbError = (message: string, error: DOMException | null): Error => {
  if (!error) {
    return new Error(message);
  }

  return new Error(`${message}: ${error.message}`, { cause: error });
};

const ensureObjectStore = (db: IDBDatabase, storeName: string): void => {
  if (!db.objectStoreNames.contains(storeName)) {
    db.createObjectStore(storeName);
  }
};

const openNativeDb = async (
  dbName: string,
  version: number | undefined,
  upgradeHandler?: (db: IDBDatabase) => void
): Promise<IDBDatabase> => {
  if (!globalThis.indexedDB) {
    throw new Error('IndexedDB is not available in this runtime');
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    let settled = false;
    const request =
      typeof version === 'number'
        ? globalThis.indexedDB.open(dbName, version)
        : globalThis.indexedDB.open(dbName);

    const rejectOnce = (error: unknown) => {
      if (settled) {
        return;
      }

      settled = true;
      reject(error instanceof Error ? error : new Error(String(error)));
    };

    request.onupgradeneeded = () => {
      if (!upgradeHandler) {
        return;
      }

      try {
        upgradeHandler(request.result);
      } catch (error) {
        request.transaction?.abort();
        rejectOnce(error);
      }
    };

    request.onsuccess = () => {
      if (settled) {
        request.result.close();
        return;
      }

      settled = true;
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onerror = () =>
      rejectOnce(createIndexedDbError(`Failed to open IndexedDB database "${dbName}"`, request.error));

    request.onblocked = () =>
      rejectOnce(
        new Error(`Opening IndexedDB database "${dbName}" was blocked by another connection`)
      );
  });
};

const openWebStoreDb = async (dbName: string, storeName: string): Promise<IWebStoreDb> => {
  let db = await openNativeDb(dbName, undefined, (upgradeDb) =>
    ensureObjectStore(upgradeDb, storeName)
  );
  if (db.objectStoreNames.contains(storeName)) {
    return new WebStoreDb(db);
  }

  const nextVersion = db.version + 1;
  db.close();
  db = await openNativeDb(dbName, nextVersion, (upgradeDb) =>
    ensureObjectStore(upgradeDb, storeName)
  );
  if (!db.objectStoreNames.contains(storeName)) {
    db.close();
    throw new Error(`Failed to create IndexedDB object store "${storeName}" in database "${dbName}"`);
  }

  return new WebStoreDb(db);
};

export class WebStore<T = any> {
  public db!: IWebStoreDb;
  public options: IWebStoreOptions;
  private initPromise?: Promise<void>;
  private lifecycleGeneration = 0;
  private closeCoveredGeneration = 0;
  private closePromise?: Promise<void>;
  private activeOperations = new Set<Promise<unknown>>();

  constructor(optionsArg: IWebStoreOptions) {
    this.options = optionsArg;
  }

  private trackOperation<TResult>(operationArg: () => Promise<TResult>): Promise<TResult> {
    this.lifecycleGeneration += 1;
    let trackedOperation!: Promise<TResult>;
    trackedOperation = (async () => operationArg())().finally(() => {
      this.activeOperations.delete(trackedOperation);
    });
    this.activeOperations.add(trackedOperation);
    return trackedOperation;
  }

  public init(optionsArg: IWebStoreOperationOptions = {}): Promise<void> {
    return this.trackOperation(() => this.initForOperation(optionsArg.signal));
  }

  private async initForOperation(signalArg?: AbortSignal): Promise<void> {
    signalArg?.throwIfAborted();
    const closeInProgress = this.closePromise;
    if (closeInProgress) {
      await awaitWithSignal(closeInProgress, signalArg);
      signalArg?.throwIfAborted();
    }
    if (!this.initPromise) {
      this.initPromise = this.runInit().catch((error) => {
        this.initPromise = undefined;
        throw error;
      });
    }
    return awaitWithSignal(this.initPromise, signalArg);
  }

  protected async runInit() {
    const smartenv = new plugins.smartenv.Smartenv();
    if (!smartenv.isBrowser && !globalThis.indexedDB) {
      await smartenv.getSafeNodeModule('fake-indexeddb/auto');
      if (!globalThis.indexedDB) {
        const mod = (await smartenv.getSafeNodeModule(
          'fake-indexeddb'
        )) as typeof import('fake-indexeddb');
        globalThis.indexedDB = new mod.IDBFactory();
      }
    }

    this.db = await openWebStoreDb(this.options.dbName, this.options.storeName);
  }

  private runDataOperation<TResult>(
    operationArg: (dbArg: IWebStoreDb) => Promise<TResult>,
    optionsArg: IWebStoreOperationOptions
  ): Promise<TResult> {
    return this.trackOperation(async () => {
      await this.initForOperation(optionsArg.signal);
      optionsArg.signal?.throwIfAborted();
      return operationArg(this.db);
    });
  }

  /**
   * Closes an initialized database without initializing an unused store.
   *
   * Operations accepted before close finish first. Operations arriving while
   * close is in progress wait for it and lazily reopen the persisted database.
   */
  public close(): Promise<void> {
    const targetGeneration = this.lifecycleGeneration;
    if (this.closePromise && targetGeneration <= this.closeCoveredGeneration) {
      return this.closePromise;
    }
    const precedingClose = this.closePromise;
    const operationsToDrain = [...this.activeOperations];
    if (!precedingClose && !this.initPromise && operationsToDrain.length === 0) {
      return Promise.resolve();
    }

    let trackedClose!: Promise<void>;
    trackedClose = (async () => {
      try {
        if (precedingClose) {
          try {
            await precedingClose;
          } catch {}
        }
        await Promise.allSettled(operationsToDrain);
        const initializationToClose = this.initPromise;
        if (!initializationToClose) {
          return;
        }
        try {
          await initializationToClose;
        } catch {
          return;
        } finally {
          if (this.initPromise === initializationToClose) {
            this.initPromise = undefined;
          }
        }
        this.db.close();
      } finally {
        if (this.closePromise === trackedClose) {
          this.closePromise = undefined;
        }
      }
    })();
    this.closePromise = trackedClose;
    this.closeCoveredGeneration = targetGeneration;
    return trackedClose;
  }

  async get(key: string, optionsArg: IWebStoreOperationOptions = {}): Promise<T> {
    return this.runDataOperation(
      (dbArg) => dbArg.get(this.options.storeName, key, optionsArg),
      optionsArg
    );
  }

  async check(
    keyArg: string,
    optionsArg: IWebStoreOperationOptions = {}
  ): Promise<boolean> {
    return this.runDataOperation(async (dbArg) => {
      const result = await dbArg.getKey(
        this.options.storeName,
        keyArg,
        optionsArg
      );
      return result !== undefined;
    }, optionsArg);
  }

  async set(
    key: string,
    val: T,
    optionsArg: IWebStoreOperationOptions = {}
  ) {
    return this.runDataOperation(
      (dbArg) => dbArg.put(this.options.storeName, val, key, optionsArg),
      optionsArg
    );
  }

  async delete(key: string, optionsArg: IWebStoreOperationOptions = {}) {
    return this.runDataOperation(
      (dbArg) => dbArg.delete(this.options.storeName, key, optionsArg),
      optionsArg
    );
  }

  async clear(optionsArg: IWebStoreOperationOptions = {}) {
    return this.runDataOperation(
      (dbArg) => dbArg.clear(this.options.storeName, optionsArg),
      optionsArg
    );
  }

  async keys(optionsArg: IWebStoreOperationOptions = {}) {
    return this.runDataOperation(
      (dbArg) => dbArg.getAllKeys(this.options.storeName, optionsArg),
      optionsArg
    );
  }
}
